import { zodResolver } from '@hookform/resolvers/zod';
import { Grid2 } from '@mui/material';
import { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import ButtonComponent from '../../../components/ButtonComponent';
import DropdownSelectComponent from '../../../components/DropdownSelectComponent';
import ModalComponent from '../../../components/ModalComponent';
import TextFieldComponent from '../../../components/TextFieldComponent';
import { AppContext } from '../../../context/AppContextProvider';

import {
  SchoolTransportationFormInput,
  SchoolTransportationFormSchema
} from '../../../services/SchoolTransportation/schemas';
import { ISchoolTransportationProps } from '../../../services/SchoolTransportation/types';

import {
  useCreateSchoolTransportationAsync,
  useUpdateSchoolTransportationAsync,
} from '../../../services/SchoolTransportation/hooks/useMutation';

import { useGetAllResponsiblesForSelectionMutateAsync } from '../../../services/Recognition/Responsible/hooks/useMutation';
import { useGetAllStudentsForSelectionMutateAsync } from '../../../services/Recognition/Student/hooks/useMutation';

interface SchoolTransportationEditionModalProps {
  isOpen: boolean;
  setIsOpen: Dispatch<SetStateAction<boolean>>;
  transportationEdit?: ISchoolTransportationProps; 
  handleClose?: () => void;
}

const SchoolTransportationEditionModal = ({
  isOpen,
  setIsOpen,
  transportationEdit,
  handleClose,
}: SchoolTransportationEditionModalProps) => {
  const { showSnackbar } = useContext(AppContext);
  const [studentsList, setStudentsList] = useState<any[]>([]);
  const [responsiblesList, setResponsiblesList] = useState<any[]>([]);

  const { mutateAsync: getStudentsSelectionAsync } = useGetAllStudentsForSelectionMutateAsync();
  const { mutateAsync: getResponsiblesSelectionAsync } = useGetAllResponsiblesForSelectionMutateAsync();
  const { mutate: createTransportation, isLoading: isCreating } = useCreateSchoolTransportationAsync();
  const { mutate: updateTransportation, isLoading: isUpdating } = useUpdateSchoolTransportationAsync();

  const {
    control,
    handleSubmit,
    reset,
    formState: { errors },
  } = useForm<SchoolTransportationFormInput>({
    resolver: zodResolver(SchoolTransportationFormSchema),
    defaultValues: {
      name: '',
      plate: '',
      brand: '',
      model: '',
      studentIds: [],
      responsibleIds: [],
    },
  });

  useEffect(() => {
    if (isOpen) {
      const loadDataAndPopulateForm = async () => {
        try {
          const [studentsData, responsiblesData] = await Promise.all([
            getStudentsSelectionAsync(undefined),
            getResponsiblesSelectionAsync(undefined)
          ]);

          const loadedStudents = studentsData || [];
          const loadedResponsibles = responsiblesData || [];

          setStudentsList(loadedStudents);
          setResponsiblesList(loadedResponsibles);

          let formData: SchoolTransportationFormInput = {
            name: '',
            plate: '',
            brand: '',
            model: '',
            studentIds: [],
            responsibleIds: [],
          };

          if (transportationEdit) {
            const currentStudentIds = transportationEdit.students?.map((s) => s.id ?? 0) || [];
            const currentResponsibleIds = transportationEdit.responsibles?.map((r) => r.id ?? 0) || [];

            formData = {
              name: transportationEdit.name || '',
              plate: transportationEdit.plate || '',
              brand: transportationEdit.brand || '',
              model: transportationEdit.model || '',
              studentIds: currentStudentIds,
              responsibleIds: currentResponsibleIds,
            };
          }

          reset(formData);
        } catch (error) {
          console.error("Erro ao carregar dados", error);
          showSnackbar("Erro ao carregar listas.", "error");
        }
      };

      loadDataAndPopulateForm();
    } else {
        reset({
            name: '', plate: '', brand: '', model: '', studentIds: [], responsibleIds: []
        });
    }
  }, [isOpen, transportationEdit?.id]); 

  const onSubmit = (data: SchoolTransportationFormInput) => {
    const onSuccess = () => {
      showSnackbar(
        transportationEdit 
          ? 'Transporte atualizado com sucesso.' 
          : 'Transporte cadastrado com sucesso.', 
        'success'
      );
      handleCloseModal();
    };

    const onError = (error: any) => {
      const msg = error?.response?.data?.message || 'Erro ao salvar transporte.';
      showSnackbar(msg, 'error');
    };

    if (transportationEdit?.id) {
      updateTransportation({ id: transportationEdit.id, data }, { onSuccess, onError });
    } else {
      createTransportation({ data }, { onSuccess, onError });
    }
  };

  const handleCloseModal = () => {
    setIsOpen(false);
    reset();
    if (handleClose) handleClose();
  };

  return (
    <ModalComponent
      isOpen={isOpen}
      onClose={handleCloseModal}
      title={transportationEdit ? 'Editar Transporte' : 'Novo Transporte'}
    >
      <Grid2 container spacing={2} sx={{ mt: 1 }}>
        
        <Grid2 size={{ xs: 12, md: 6 }}>
          {/* Usando onChangeText manual pois o TextFieldComponent atual não propaga a ref/onChange padrão corretamente */}
          <Controller
            name="name"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextFieldComponent
                label="Nome *"
                errorMessage={errors.name?.message}
                value={value || ''}
                onChangeText={(newValue) => onChange(newValue)}
              />
            )}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            name="plate"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextFieldComponent
                label="Placa *"
                errorMessage={errors.plate?.message}
                value={value || ''}
                onChangeText={(newValue) => onChange(newValue)}
              />
            )}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            name="brand"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextFieldComponent
                label="Marca"
                errorMessage={errors.brand?.message}
                value={value || ''}
                onChangeText={(newValue) => onChange(newValue)}
              />
            )}
          />
        </Grid2>
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            name="model"
            control={control}
            render={({ field: { onChange, value } }) => (
              <TextFieldComponent
                label="Modelo"
                errorMessage={errors.model?.message}
                value={value || ''}
                onChangeText={(newValue) => onChange(newValue)}
              />
            )}
          />
        </Grid2>

        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            name="responsibleIds"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DropdownSelectComponent
                label="Responsáveis"
                multiple
                options={responsiblesList}
                valueField="id"
                labelField="name"
                selectedValues={value || []}
                onSelectedValues={onChange}
                errorMessage={errors.responsibleIds?.message}
              />
            )}
          />
        </Grid2> 
        <Grid2 size={{ xs: 12, md: 6 }}>
          <Controller
            name="studentIds"
            control={control}
            render={({ field: { value, onChange } }) => (
              <DropdownSelectComponent
                label="Alunos"
                multiple
                options={studentsList}
                valueField="id"
                labelField="name"
                selectedValues={value || []} 
                onSelectedValues={onChange}
                errorMessage={errors.studentIds?.message}
              />
            )}
          />
        </Grid2>

      </Grid2>

      <ButtonComponent
        fullWidth
        sx={{ mt: 3 }}
        onClick={handleSubmit(onSubmit)}
        loading={isCreating || isUpdating}
      >
        Salvar
      </ButtonComponent>
    </ModalComponent>
  );
};

export default SchoolTransportationEditionModal;
